import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Timer, Power } from 'lucide-react';
import { api } from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import IntelligenceSummaryStrip from '@/components/intelligence/IntelligenceSummaryStrip';
import DataSourcesStatusPanel from '@/components/intelligence/DataSourcesStatusPanel';
import DataQualityAlertsSection from '@/components/intelligence/DataQualityAlertsSection';
import PriceDriftAlertsSection from '@/components/intelligence/PriceDriftAlertsSection';
import BusinessInsightsSection from '@/components/intelligence/BusinessInsightsSection';
import CostStructureSection from '@/components/intelligence/CostStructureSection';
import SourceConflictSection from '@/components/intelligence/SourceConflictSection';
import TimeIntegrityIndicator from '@/components/intelligence/TimeIntegrityIndicator';

const INTERVALS = [
  { label: '30s', value: 30 },
  { label: '60s', value: 60 },
  { label: '2m', value: 120 },
];

const SectionLabel = ({ label, currentColors }) => (
  <div className="flex items-center gap-3 mb-3 mt-2">
    <div className="h-px flex-1" style={{ background: currentColors.border }} />
    <span
      className="text-xs font-semibold uppercase tracking-widest px-2"
      style={{ color: currentColors.textSecondary }}
    >
      {label}
    </span>
    <div className="h-px flex-1" style={{ background: currentColors.border }} />
  </div>
);

const IntelligenceHub = () => {
  const { theme } = useTheme();
  const currentColors = colors[theme];

  // ── Shared data state ──
  const [dataQuality, setDataQuality] = useState(null);
  const [dqLoading, setDqLoading] = useState(true);

  // ── Auto-refresh state ──
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // ── Initial load (shows spinners) ──
  const loadDataQuality = useCallback(async () => {
    setDqLoading(true);
    try {
      const res = await api.getStructuredAlerts();
      setDataQuality(res.data);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Error loading data quality:', e);
    } finally {
      setDqLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDataQuality();
  }, [loadDataQuality]);

  // ── Silent refresh (no spinners, background update) ──
  const silentRefresh = useCallback(async () => {
    try {
      const [dqRes] = await Promise.allSettled([
        api.getStructuredAlerts(),
      ]);
      if (dqRes.status === 'fulfilled') setDataQuality(dqRes.value.data);
      setRefreshKey((k) => k + 1);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Silent refresh error:', e);
    }
  }, []);

  // ── Auto-refresh timer ──
  useEffect(() => {
    if (!autoRefresh || modalOpen) return;
    const timer = setInterval(silentRefresh, refreshInterval * 1000);
    return () => clearInterval(timer);
  }, [autoRefresh, refreshInterval, modalOpen, silentRefresh]);

  const handleModalStateChange = useCallback((isOpen) => {
    setModalOpen(isOpen);
  }, []);

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="intelligence-hub-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <h1 className="text-3xl md:text-4xl font-bold mb-1" style={{ color: currentColors.textPrimary }}>
          Intelligence & Analytics
        </h1>
        <p className="text-sm" style={{ color: currentColors.textSecondary }}>
          Unified operational cockpit — critical issues surface first
        </p>
      </motion.div>

      {/* ── Auto-refresh Controls ── */}
      <motion.div
        className="flex items-center gap-3 mb-6 flex-wrap"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        data-testid="auto-refresh-controls"
      >
        {/* Toggle */}
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
          style={{
            background: autoRefresh ? currentColors.accentGreen + '20' : currentColors.background,
            color: autoRefresh ? currentColors.accentGreen : currentColors.textSecondary,
            border: `1px solid ${autoRefresh ? currentColors.accentGreen + '60' : currentColors.border}`,
          }}
          data-testid="auto-refresh-toggle"
        >
          <Power size={12} />
          Live {autoRefresh ? 'ON' : 'OFF'}
        </button>

        {/* Interval pills */}
        {autoRefresh && (
          <div
            className="flex items-center rounded-lg overflow-hidden"
            style={{ border: `1px solid ${currentColors.border}` }}
            data-testid="refresh-interval-selector"
          >
            {INTERVALS.map((iv) => (
              <button
                key={iv.value}
                onClick={() => setRefreshInterval(iv.value)}
                className="px-3 py-1.5 text-xs font-medium transition-colors"
                style={{
                  background: refreshInterval === iv.value ? currentColors.accentBlue : 'transparent',
                  color: refreshInterval === iv.value ? '#fff' : currentColors.textSecondary,
                }}
                data-testid={`refresh-interval-${iv.value}`}
              >
                {iv.label}
              </button>
            ))}
          </div>
        )}

        {/* Manual refresh */}
        <button
          onClick={silentRefresh}
          className="p-1.5 rounded-lg hover:opacity-70 transition-opacity"
          style={{ color: currentColors.textSecondary }}
          title="Refresh now"
          data-testid="manual-refresh-btn"
        >
          <RefreshCw size={14} />
        </button>

        {/* Last updated */}
        {lastUpdated && (
          <span
            className="text-xs flex items-center gap-1.5"
            style={{ color: currentColors.textSecondary }}
            data-testid="last-updated-timestamp"
          >
            <Timer size={12} />
            {lastUpdated.toLocaleTimeString()}
          </span>
        )}

        {/* Paused indicator */}
        {autoRefresh && modalOpen && (
          <span
            className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
            style={{ background: currentColors.accentYellow + '20', color: currentColors.accentYellow }}
            data-testid="refresh-paused-indicator"
          >
            Paused — interaction active
          </span>
        )}
      </motion.div>

      {/* ── KPI Summary Strip ── */}
      <IntelligenceSummaryStrip dataQuality={dataQuality} refreshKey={refreshKey} />

      {/* ── Data Sources & Upstream Status ── */}
      <DataSourcesStatusPanel
        coverage={dataQuality?.coverage}
        scannedAt={dataQuality?.scanned_at}
        itemsScanned={dataQuality?.summary?.total_items_scanned}
      />

      {/* ── Critical Integrity Alerts ── */}
      <SectionLabel label="Critical Integrity" currentColors={currentColors} />
      <TimeIntegrityIndicator refreshKey={refreshKey} />
      <DataQualityAlertsSection data={dataQuality} loading={dqLoading} onRefresh={loadDataQuality} />

      {/* ── Active Operational Risk ── */}
      <SectionLabel label="Active Operational Risk" currentColors={currentColors} />
      <PriceDriftAlertsSection onModalStateChange={handleModalStateChange} />
      <SourceConflictSection refreshKey={refreshKey} />

      {/* ── Interpretation Metrics ── */}
      <SectionLabel label="Interpretation Metrics" currentColors={currentColors} />
      <CostStructureSection refreshKey={refreshKey} />
      <BusinessInsightsSection onModalStateChange={handleModalStateChange} />
    </div>
  );
};

export default IntelligenceHub;
